import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Col, Container, Row } from 'react-bootstrap';
import { CiMenuKebab } from 'react-icons/ci';
import { RiFolderOpenFill } from 'react-icons/ri';
import {
  MdContentCopy,
  MdContentCut,
  MdContentPaste,
  MdDelete,
  MdDownload,
  MdEdit,
  MdFolderOpen,
  MdUploadFile,
} from 'react-icons/md';
import { toast } from 'react-toastify';

import { apiHeader, BaseURL } from '../../../config/apiUrl';
import { Get, Patch, Post } from '../../../Axios/AxiosFunctions';
import { moment } from '../../../utils/timeUtils';
import SearchInput from '../../../Component/SearchInput';
import { Checkbox } from '../../../Component/Checkbox/Checkbox';
import Loader from '../../../Component/Loader';
import { Button } from '../../../Component/Button/Button';
import CreateFolderModal from '../../../modals/CreateFolderModal';
import AreYouSureModal from '../../../modals/AreYouSureModal';
import { IconByExtension } from '../../TeamFolder/IconByExtension';
import classes from './AdminDocuments.module.css';

const NoFolderComp = () => (
  <div className={classes.noFolderComp}>
    <RiFolderOpenFill />
    <p>Drag + Drop Files Here</p>
  </div>
);

const AdminDocuments = ({ onBack }) => {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer,
  );

  const inputRef = useRef(null);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [files, setFiles] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [showModal, setShowModal] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [mainItems, setMainItems] = useState([]);
  const [action, setAction] = useState(''); // 'copy' | 'cut' | ''

  // ─── Fetch ─────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!accessToken || !companyId) return;
    setIsPageLoading(true);
    try {
      const url = BaseURL('admin-documents');
      const response = await Get(url, accessToken, companyId);
      if (response?.data?.status === 'success') {
        const data = response.data.data || [];
        setAllItems(data);

        // Process into tree structure with parents array
        const root = data.find((item) => !item.parent);
        if (root) {
          const processed = processData(data, { ...root, parent: null, parents: [] });
          setItems(processed);
        }
      }
    } catch (err) {
      console.error('Failed to fetch admin documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setIsPageLoading(false);
    }
  }, [accessToken, companyId]);

  const processData = (allData, node) => {
    let result = [node];
    if (node.children && node.children.length > 0) {
      const childIds = node.children.map((c) => (typeof c === 'string' ? c : c._id));
      childIds.forEach((childId) => {
        const child = allData.find((item) => item._id === childId);
        if (child) {
          const d = {
            ...child,
            parent: node._id,
            parents: [...(node.parents || []), node._id],
          };
          result = [...result, ...processData(allData, d)];
        }
      });
    }
    return result;
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Derive files for selected folder ──────────────────────────
  useEffect(() => {
    if (selectedFolder) {
      setFiles(
        items.filter(
          (item) => item.isFile && item.parent === selectedFolder._id,
        ),
      );
    } else {
      setFiles([]);
    }
  }, [selectedFolder, items]);

  // Auto-expand root and select it
  useEffect(() => {
    if (items.length > 0 && !selectedFolder) {
      setSelectedFolder(items[0]);
      setSelectedItem(items[0]);
      setExpanded([items[0]._id]);
    }
  }, [items]);

  // ─── Search ────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const reg = new RegExp(search, 'i');
    const matched = items.filter((item) =>
      reg.test(item.isFile ? item.fileName : item.name),
    );
    const ids = new Set(matched.map((m) => m._id));
    matched.forEach((m) => {
      (m.parents || []).forEach((p) => ids.add(p));
    });
    return items.filter((item) => ids.has(item._id) || !item.parent);
  }, [items, search]);

  // ─── Icons ─────────────────────────────────────────────────────
  const getIcon = (node) => {
    let ext = 'folder';
    if (node.isFile) {
      ext = (node.name || '').split('.').pop() || 'file';
    } else if (node.children?.length > 0) {
      ext = 'folderopen';
    }
    return <IconByExtension extension={ext} />;
  };

  // ─── Item select (checkbox) ────────────────────────────────────
  const handleItemSelect = (event, node) => {
    event.stopPropagation();
    const isSelected = selectedItems.some((i) => i._id === node._id);
    if (isSelected) {
      const filtered = mainItems.filter((i) => i._id !== node._id);
      setMainItems(filtered);
      setSelectedItems(filtered);
    } else {
      const filtered = [...mainItems, node];
      setMainItems(filtered);
      setSelectedItems(filtered);
    }
  };

  // ─── Kebab menu click ─────────────────────────────────────────
  const onClickMenu = (event, node) => {
    event.stopPropagation();
    setSelectedItem(node);
    setAnchorEl(event.currentTarget);
  };

  // ─── Validation helpers ────────────────────────────────────────
  const canCreate = (node = selectedItem) => {
    return node && !node.isFile;
  };

  const canModify = (node = selectedItem) => {
    return node && node.parent;
  };

  // ─── List item (reused in tree + file panel) ───────────────────
  const listItem = (node) => (
    <div
      className={classes.itemsContainer}
      onDrop={(e) => !node.isFile && onDrop(e, node)}
      onDragOver={onDragOver}
    >
      {canModify(node) && (
        <Checkbox
          className={classes.checkbox}
          value={selectedItems.some((i) => i._id === node._id) ? ' ' : ''}
          label={' '}
          setValue={(value, event) => handleItemSelect(event, node)}
          key={node._id}
        />
      )}
      {getIcon(node)}
      <div className={classes.descContainer}>
        <div
          className={[classes.descName, node.isFile ? classes.fileName : ''].join(' ')}
          onClick={() => node.isFile && handleDownloadFile(node)}
        >
          {node.isFile ? node.fileName : node.name}
        </div>
        <div className={classes.descDate}>
          🕑{moment(node.createdAt).format('MM-DD-YYYY hh:mm')}
        </div>
      </div>
      <CiMenuKebab
        size={20}
        className={classes.menuIcon}
        onClick={(e) => onClickMenu(e, node)}
      />
    </div>
  );

  // ─── Tree rendering ────────────────────────────────────────────
  const renderTree = (node) => {
    if (!node || node.isFile) return null;
    const childFolders = filteredItems.filter(
      (item) => !item.isFile && node.children?.includes(item._id),
    );

    return (
      <TreeItem
        key={node._id}
        nodeId={node._id}
        label={listItem(node)}
        onClick={() => {
          setSelectedFolder(node);
          setSelectedItem(node);
        }}
      >
        {childFolders.length === 0 ? <></> : childFolders.map((child) => renderTree(child))}
      </TreeItem>
    );
  };

  // ─── Actions ───────────────────────────────────────────────────

  const handleCreateFolder = async (formData) => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const url = BaseURL('admin-documents');
      const response = await Post(
        url,
        { name: formData.name, parent: selectedItem._id },
        apiHeader(accessToken),
        companyId,
      );
      if (response?.data?.status === 'success') {
        toast.success('Folder created successfully');
        setShowModal('');
        await fetchData();
      }
    } catch (err) {
      toast.error('Failed to create folder');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFiles = async (fileList, node = selectedFolder) => {
    if (!node) return;
    const fileArr = Array.from(fileList);
    if (!fileArr.length) return toast.error('File is required');

    setLoading(true);
    try {
      const url = BaseURL('admin-documents');
      const formData = new FormData();
      formData.append('parent', node._id);
      fileArr.forEach((f) => formData.append('file', f));

      const response = await Post(url, formData, apiHeader(accessToken), companyId);
      if (response?.data?.status === 'success') {
        toast.success('File uploaded successfully');
        await fetchData();
      }
    } catch (err) {
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (formData) => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const url = BaseURL('admin-documents/update');
      const body = {
        folderId: selectedItem._id,
        ...(selectedItem.isFile ? { fileName: formData.name } : { name: formData.name }),
      };
      const response = await Patch(url, body, apiHeader(accessToken));
      if (response?.data?.status === 'success') {
        toast.success('Renamed successfully');
        setShowModal('');
        await fetchData();
      }
    } catch (err) {
      toast.error('Failed to rename');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItems = async () => {
    const itemsToDelete = mainItems.length > 0 ? mainItems : (selectedItem ? [selectedItem] : []);
    if (!itemsToDelete.length) return;

    setShowDeleteModal(false);
    setLoading(true);
    try {
      const parent = itemsToDelete[0].parent;
      const response = await Post(
        BaseURL('admin-documents/delete-items'),
        { parent, items: itemsToDelete },
        apiHeader(accessToken),
        companyId,
      );
      if (response?.data?.status === 'success') {
        toast.success('Deleted successfully');
        setMainItems([]);
        setSelectedItems([]);
        await fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyClicked = () => {
    setAction('copy');
    toast.success('Selected items to copy', { autoClose: 1000 });
  };

  const handleCutClicked = () => {
    setAction('cut');
    toast.success('Selected items to cut', { autoClose: 1000 });
  };

  const handlePaste = async () => {
    if (!selectedItem || selectedItem.isFile) return;
    if (!mainItems.length) return;
    const url = BaseURL(action === 'copy' ? 'admin-documents/copy' : 'admin-documents/cut');
    const params = { parent: selectedItem._id, items: mainItems.map((i) => i._id) };
    setLoading(true);
    try {
      const response = await Post(url, params, apiHeader(accessToken), companyId);
      if (response?.data?.status === 'success') {
        toast.success('Success', { autoClose: 1500 });
        setAction('');
        setMainItems([]);
        setSelectedItems([]);
        await fetchData();
      }
    } catch (err) {
      toast.error('Paste failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (node) => {
    try {
      const url = BaseURL(`admin-documents/file/${node._id}`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'company-id': companyId,
        },
      });
      if (!response.ok) {
        toast.error('Download failed');
        return;
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = node.fileName || node.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const getPreviewUrl = (node) => {
    return BaseURL(`admin-documents/preview/${node._id}`);
  };

  // ─── Drag & drop ───────────────────────────────────────────────
  const onDrop = (event, node = selectedFolder) => {
    event.preventDefault();
    if (node && !node.isFile) {
      handleUploadFiles(event.dataTransfer.files, node);
    }
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  // ─── Menu actions ──────────────────────────────────────────────
  const onCreateFolder = () => {
    setAnchorEl(null);
    setShowModal('folder');
  };

  const onUploadFile = () => {
    setAnchorEl(null);
    setSelectedFolder(selectedItem);
    inputRef.current?.click();
  };

  const onRename = () => {
    setAnchorEl(null);
    setShowModal('rename');
  };

  const onDeleteItem = () => {
    setAnchorEl(null);
    if (!selectedItems.some((i) => i._id === selectedItem._id)) {
      setMainItems([selectedItem]);
      setSelectedItems([selectedItem]);
    }
    setShowDeleteModal(true);
  };

  const onCopyItem = () => {
    setAnchorEl(null);
    if (!selectedItems.some((i) => i._id === selectedItem._id)) {
      setMainItems([selectedItem]);
      setSelectedItems([selectedItem]);
    }
    handleCopyClicked();
  };

  const onCutItem = () => {
    setAnchorEl(null);
    if (!selectedItems.some((i) => i._id === selectedItem._id)) {
      setMainItems([selectedItem]);
      setSelectedItems([selectedItem]);
    }
    handleCutClicked();
  };

  const onPasteItem = () => {
    setAnchorEl(null);
    handlePaste();
  };

  const onDownloadItem = () => {
    setAnchorEl(null);
    handleDownloadFile(selectedItem);
  };

  const isCreateValid = canCreate(selectedItem);
  const isModifyValid = canModify(selectedItem);
  const isPasteValid =
    selectedItem &&
    !selectedItem.isFile &&
    mainItems.length > 0 &&
    action &&
    !selectedItems.some((i) => i._id === selectedItem._id);

  // ─── Render ────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .MuiTreeItem-label {
          width: calc(100% - 41px) !important;
        }
      `}</style>
      <div className={classes.mainContainer}>
        <div className={classes.headerContainer}>
          <Row>
            <Col md={8} sm={12}>
              <h3>Admin Documents</h3>
            </Col>
            <Col md={4} sm={12}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Button label="Back" onClick={onBack} />
                <SearchInput
                  setter={setSearch}
                  value={search}
                  placeholder="Search"
                  customStyle={{ width: '100%' }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <Container fluid className={classes.mainBody}>
        {loading || isPageLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            No documents found.
          </div>
        ) : (
          <Row>
            {/* Left: Folder Tree */}
            <Col lg={6} className={classes.treeContainer}>
              <div
                className={classes.treeviewContainer}
                style={{ height: 'calc(100vh - 222px)' }}
              >
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon style={{ fontSize: '1.5em' }} />}
                  defaultExpandIcon={<ChevronRightIcon style={{ fontSize: '1.5em' }} />}
                  expanded={expanded}
                  onNodeToggle={(_, ids) => setExpanded(ids)}
                  selected={selectedFolder?._id || ''}
                  className={classes.treeview}
                >
                  {items[0] && renderTree(items[0])}
                </TreeView>
              </div>
            </Col>

            {/* Right: File Panel */}
            <Col lg={6}>
              <div
                className={classes.listBody}
                onDrop={onDrop}
                onDragOver={onDragOver}
              >
                <div className={classes.uploadContainer}>
                  <Button
                    label="Upload Files"
                    disabled={!selectedFolder}
                    className={classes.submit_btn}
                    onClick={() => inputRef.current?.click()}
                  />
                  <input
                    hidden
                    multiple
                    type="file"
                    ref={inputRef}
                    onChange={(e) => {
                      handleUploadFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  {files.length > 0 && <div>Drag + Drop Files Here</div>}
                </div>
                <div
                  className={classes.listContainer}
                  style={{ height: 'calc(100vh - 302px)' }}
                >
                  {files.length > 0 ? (
                    files.map((node) => listItem(node))
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <NoFolderComp />
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>

      {/* Context Menu */}
      {Boolean(anchorEl) && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {!isCreateValid && !isModifyValid && !isPasteValid && (
            <MenuItem className={classes.menuItem}>...</MenuItem>
          )}
          {isCreateValid && (
            <MenuItem className={classes.menuItem} onClick={onCreateFolder}>
              <MdFolderOpen className={classes.mIcons} /> Create Folder
            </MenuItem>
          )}
          {isCreateValid && (
            <MenuItem className={classes.menuItem} onClick={onUploadFile}>
              <MdUploadFile className={classes.mIcons} /> Upload Files
            </MenuItem>
          )}
          {isModifyValid && (
            <MenuItem className={classes.menuItem} onClick={onRename}>
              <MdEdit className={classes.mIcons} /> Rename
            </MenuItem>
          )}
          {isModifyValid && (
            <MenuItem className={classes.menuItem} onClick={onCopyItem}>
              <MdContentCopy className={classes.mIcons} /> Copy
            </MenuItem>
          )}
          {isModifyValid && (
            <MenuItem className={classes.menuItem} onClick={onCutItem}>
              <MdContentCut className={classes.mIcons} /> Cut
            </MenuItem>
          )}
          {isPasteValid && (
            <MenuItem className={classes.menuItem} onClick={onPasteItem}>
              <MdContentPaste className={classes.mIcons} /> Paste
            </MenuItem>
          )}
          {selectedItem?.isFile && (
            <MenuItem className={classes.menuItem} onClick={onDownloadItem}>
              <MdDownload className={classes.mIcons} /> Download
            </MenuItem>
          )}
          {isModifyValid && (
            <MenuItem className={[classes.menuItem, classes.menuDelete].join(' ')} onClick={onDeleteItem}>
              <MdDelete className={classes.mIcons} /> Delete
            </MenuItem>
          )}
        </Menu>
      )}

      {/* Create / Rename Folder Modal */}
      <CreateFolderModal
        show={['folder', 'rename'].includes(showModal)}
        setShow={() => setShowModal('')}
        isLoading={loading}
        data={showModal === 'rename' ? selectedItem : undefined}
        handleSubmit={showModal === 'folder' ? handleCreateFolder : handleRename}
      />

      {/* Delete Confirmation */}
      {showDeleteModal && (
        <AreYouSureModal
          isApiCall={false}
          setShow={setShowDeleteModal}
          show={showDeleteModal}
          onClick={handleDeleteItems}
          subTitle="Do you really want to delete?"
        />
      )}
    </>
  );
};

export default AdminDocuments;
